/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/label-has-associated-control */
import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import Select, { addNullOption } from 'admin/components/atoms/Select';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import SchemaFilterForm from 'admin/components/shared/SchemaFilterForm';
import { ColorKey, GradientKey } from 'admin/theme/tokens/colors';
import { Moment } from 'moment';
import React from 'react';
import styled, { css } from 'styled-components';
import type { QueryParams } from '..';
import AdminUser from '../../../../models/admin_user';
import { Button } from '../../../atoms/Buttons';
import LabeledTextBox from '../../../molecules/LabeledTextBox';
import SelectedPrivateMoneyDisplay from '../../../organisms/SelectedPrivateMoneyDisplay';

type Props = {
  filterParams: QueryParams;
  onChangeParams: (params: Partial<QueryParams>) => void;
  onSearch: () => void;
  onClear: () => void;
  onChangeQuery: (key: PropertyKey, value: unknown) => void;
  selectedMoney: PrivateMoney;
};

type PrivateMoney = NonNullable<AdminUser['privateMoneys']>[number];

type AccountStatusValue = 'active' | 'suspended' | 'pre-closed' | 'closed';
type AccountStatusOption = { label: string; value: AccountStatusValue | null };

const statusMap: Record<AccountStatusValue, { color: ColorKey; gradient?: GradientKey }> = {
  active: { color: 'green', gradient: '300' },
  suspended: { color: 'white' },
  'pre-closed': { color: 'gray', gradient: '200' },
  closed: { color: 'gray', gradient: '800' },
};

const ACCOUNT_STATUS: AccountStatusOption[] = addNullOption([
  { label: '有効', value: 'active' as AccountStatusValue },
  { label: '凍結中', value: 'suspended' as AccountStatusValue },
  { label: '退会準備中', value: 'pre-closed' as AccountStatusValue },
  { label: '退会済', value: 'closed' as AccountStatusValue },
]);

// UI では「住民認証済」を1つの選択肢に畳み込む。API 上は
// individual_number_identification と resident_status の直交した2パラメータ
type IndividualNumberIdentificationValue = 'identified' | 'resident' | 'not-identified';
type IndividualNumberIdentificationOption = {
  label: string;
  value: IndividualNumberIdentificationValue | null;
};

const INDIVIDUAL_NUMBER_IDENTIFICATION: IndividualNumberIdentificationOption[] = addNullOption([
  { label: '認証済', value: 'identified' as IndividualNumberIdentificationValue },
  { label: '住民認証済', value: 'resident' as IndividualNumberIdentificationValue },
  { label: '未認証', value: 'not-identified' as IndividualNumberIdentificationValue },
]);

const Wrapper = styled.div`
  display: block;
  flex-wrap: wrap;

  ${Select as any} {
    width: 300px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const LabelText = styled.span`
  font-weight: 400;
`;

const SelectOptionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CircleStatusIndicator = styled.div<{ $status: AccountStatusValue | null }>`
  ${({ theme: { colors, borderRadius }, $status }) => {
    // for null option alignment with other options that have a colored indicator
    if (!$status) {
      return css`
        width: 15px;
        height: 15px;
        border-radius: ${borderRadius.full};
        display: inline-block;
        background-color: transparent;
        border: 1px solid transparent;
      `;
    }

    const { color, gradient } = statusMap[$status];
    const backgroundColor =
      gradient && color !== 'white' && color !== 'black' ? colors[color][gradient] : colors[color];
    const borderColor = $status === 'suspended' ? colors.gray['200'] : 'transparent';

    return css`
      width: 15px;
      height: 15px;
      border-radius: ${borderRadius.full};
      display: inline-block;
      background-color: ${backgroundColor};
      border: 1px solid ${borderColor};
    `;
  }}
`;

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  margin-right: 1rem;
  ${Button} {
    margin-left: 10px;
  }
`;

const FilterForm = ({
  filterParams,
  onChangeParams,
  onSearch,
  onClear,
  onChangeQuery,
  selectedMoney,
}: Props) => {
  const formatOptionLabel = (option: AccountStatusOption) => {
    return (
      <SelectOptionWrapper>
        <CircleStatusIndicator $status={option.value} />
        <div>{option.label}</div>
      </SelectOptionWrapper>
    );
  };

  // URL 直叩きやブックマークで resident_status だけが載っていることがある。
  // セレクトの選択肢に畳み込めない組み合わせでも「すべて」とは表示しない
  // (絞り込みが効いているのに解除手段が無くなるため)
  const individualNumberIdentificationValue: IndividualNumberIdentificationValue | null =
    filterParams.resident_status === 'resident'
      ? 'resident'
      : filterParams.individual_number_identification;

  const onIndividualNumberIdentificationChange = (
    opt: IndividualNumberIdentificationOption | null
  ) => {
    if (opt?.value === 'resident') {
      // Not filtered by validity because the residency judgement outlives expiry.
      // Pinning identified as well would drop some of the customers the list badges
      // as 住民認証済 (issue #7623).
      onChangeParams({ individual_number_identification: null, resident_status: 'resident' });
    } else if (opt?.value) {
      onChangeParams({ individual_number_identification: opt.value, resident_status: null });
    } else {
      onChangeParams({ individual_number_identification: null, resident_status: null });
    }
  };

  const lastTransactionDateRange = {
    from: filterParams.last_transaction_from,
    to: filterParams.last_transaction_to,
  };

  const registeredDateRange = {
    from: filterParams.registered_from,
    to: filterParams.registered_to,
  };

  const onLastTransactionDatesChange = (date: { from: Moment | null; to: Moment | null }) => {
    onChangeParams({
      last_transaction_from: date.from,
      last_transaction_to: date.to,
    });
  };

  const onRegisteredDatesChange = (date: { from: Moment | null; to: Moment | null }) => {
    onChangeParams({
      registered_from: date.from,
      registered_to: date.to,
    });
  };

  return (
    <Wrapper>
      <SelectedPrivateMoneyDisplay />
      <FormWrapper>
        <FormGroup>
          <LabeledSelect
            labelText="状態"
            htmlFor="account_status"
            name="account_status"
            id="account_status"
            options={ACCOUNT_STATUS}
            value={ACCOUNT_STATUS.find((opt) => opt.value === filterParams.account_status)}
            onChange={(opt: AccountStatusOption) =>
              onChangeParams({ account_status: opt?.value ?? null })
            }
            formatOptionLabel={formatOptionLabel}
          />
        </FormGroup>
        <FormGroup>
          <LabeledSelect
            labelText="マイナンバー認証"
            htmlFor="individual_number_identification"
            name="individual_number_identification"
            id="individual_number_identification"
            options={INDIVIDUAL_NUMBER_IDENTIFICATION}
            value={INDIVIDUAL_NUMBER_IDENTIFICATION.find(
              (opt) => opt.value === individualNumberIdentificationValue
            )}
            onChange={onIndividualNumberIdentificationChange}
          />
        </FormGroup>
        <FormGroup>
          <LabeledTextBox
            labelText={<LabelText>お客様ID</LabelText>}
            htmlFor="customer_id"
            name="customer_id"
            id="customer_id"
            syncValue={filterParams.customer_id}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeParams({ customer_id: e.target.value })
            }
          />
        </FormGroup>
        <FormGroup>
          <LabeledTextBox
            labelText="ニックネーム"
            htmlFor="customer_name"
            name="customer_name"
            id="customer_name"
            syncValue={filterParams.customer_name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeParams({ customer_name: e.target.value })
            }
          />
        </FormGroup>
        <FormGroup>
          <LabeledTextBox
            labelText="携帯電話番号"
            htmlFor="customer_tel"
            name="customer_tel"
            id="customer_tel"
            syncValue={filterParams.customer_tel}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeParams({ customer_tel: e.target.value })
            }
          />
        </FormGroup>
        <FormGroup>
          <LabeledTextBox
            labelText="メールアドレス"
            htmlFor="customer_email"
            name="customer_email"
            id="customer_email"
            syncValue={filterParams.customer_email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeParams({ customer_email: e.target.value })
            }
          />
        </FormGroup>
        <FormGroup>
          <label htmlFor="registered_from" style={{ margin: '0 5px' }}>
            会員登録日時
          </label>
          <DateTimeRange
            dateRange={registeredDateRange}
            setDateRange={onRegisteredDatesChange}
            validTypeTo="invalidFromBeforeDay"
            wrapperStyles={{ marginBottom: '0', alignItems: 'center' }}
            dateTimeProps={{ toProps: { showIcon: true }, fromProps: { showIcon: true } }}
          />
        </FormGroup>
        <FormGroup>
          <label htmlFor="last_transaction_from" style={{ margin: '0 5px' }}>
            最終取引日時
          </label>
          <DateTimeRange
            dateRange={lastTransactionDateRange}
            setDateRange={onLastTransactionDatesChange}
            wrapperStyles={{ marginBottom: '0', alignItems: 'center' }}
            dateTimeProps={{ toProps: { showIcon: true }, fromProps: { showIcon: true } }}
          />
        </FormGroup>
        <FormGroup>
          <LabeledTextBox
            labelText="外部ID"
            htmlFor="external_id"
            name="external_id"
            id="external_id"
            syncValue={filterParams.external_id}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeParams({ external_id: e.target.value })
            }
          />
        </FormGroup>
      </FormWrapper>
      {selectedMoney && (
        <SchemaFilterForm
          onChangeQuery={onChangeQuery}
          selectedMoney={selectedMoney}
          isClear={Object.keys(filterParams.user_attribute_queries).length === 0}
          initialQueries={filterParams.user_attribute_queries}
        />
      )}
      <FormGroup>
        <Button variant="neutral" onClick={onClear}>
          クリア
        </Button>
        <Button onClick={onSearch}>検索</Button>
      </FormGroup>
    </Wrapper>
  );
};

export default FilterForm;
